import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import AddIcon from "@mui/icons-material/Add";

import { useAuth } from "../../../context/AuthContext";
import AppDialog from "../../General/Dialog/AppDialog";
import UploadSacAttachmentSingle from "../../LegalFileAttachment/UploadSacAttachmentSingle";

import GlassCard from "./GlassCard";
import SectionTitle from "./SectionTitle";
import AttachmentMovementsDialog from "./AttachmentMovementsDialog";
import RetryEscritoButton from "../../LegalFileAttachment/RetryEscritoButton";
import {
  ATTACHMENT_STATUS_LABELS,
  statusChipSx,
  timeAgo,
  isLegalFileLocked,
} from "./detailHelpers";

export default function TabEscritos({ attachments, legalFile, onRefresh, onPreviewAttachment }) {
  const { hasPermission } = useAuth();
  const locked = isLegalFileLocked(legalFile);

  const [openUploadSingleDialog, setOpenUploadSingleDialog] = useState(false);
  const [movementsOpen, setMovementsOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const openMovements = (attachment) => {
    setSelectedAttachment(attachment);
    setMovementsOpen(true);
  };

  const closeMovements = () => {
    setMovementsOpen(false);
    setSelectedAttachment(null);
  };

  return (
    <>
      <GlassCard sx={{ mb: 1.5 }}>
        <SectionTitle
          icon={<InsertDriveFileIcon />}
          title="Escritos"
          description="Archivos adjuntos cargados o generados para este expediente."
          right={
            hasPermission("cargar archivos adjuntos generados expediente") && !locked && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenUploadSingleDialog(true)}
                sx={{ borderRadius: 2.2, fontWeight: 800, textTransform: "none" }}
              >
                Subir en línea
              </Button>
            )
          }
        />
      </GlassCard>
      {!attachments.length ? (
        <GlassCard>
          <Typography sx={{ fontWeight: 950 }}>
            No hay Escritos
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Cuando se carguen o generen archivos, van a aparecer acá.
          </Typography>
        </GlassCard>
      ) : (
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}
        >
          {attachments.map((a) => {
            const status = a.pivot?.status || a.status;
            const dateLabel = a.created_at
              ? new Date(a.created_at).toLocaleDateString("es-AR")
              : "—";
            const ago = timeAgo(a.created_at);

            return (
              <GlassCard key={a.id} sx={{ p: { xs: 1.4, sm: 1.7 } }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.2,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(0,0,0,0.04)",
                      flexShrink: 0,
                    }}
                  >
                    <InsertDriveFileIcon />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950 }} noWrap>
                      {a.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.75 }}
                      noWrap
                    >
                      {a.type?.name || "—"} · {ago ? `${ago} · ` : ""}
                      {dateLabel}
                    </Typography>

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        size="small"
                        label={
                          ATTACHMENT_STATUS_LABELS[status] || "—"
                        }
                        sx={statusChipSx(status)}
                      />
                      {a.type?.name ? (
                        <Chip
                          size="small"
                          label={String(a.type.name).toUpperCase()}
                          sx={{
                            height: 22,
                            borderRadius: 999,
                            bgcolor: "rgba(0,0,0,0.06)",
                            fontWeight: 900,
                            "& .MuiChip-label": {
                              px: 1,
                              fontSize: 12,
                            },
                          }}
                        />
                      ) : null}
                    </Box>
                    {["failed", "error"].includes(status) && a.pivot?.error_message && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.6, color: "error.main", wordBreak: "break-word" }}
                      >
                        Motivo: {a.pivot.error_message}
                      </Typography>
                    )}
                  </Box>

                  {status === "failed" && legalFile && (
                    <RetryEscritoButton
                      attachmentId={a.id}
                      legalFileId={legalFile.id}
                      escritoName={a.name}
                      onDone={onRefresh}
                    />
                  )}

                  <Tooltip title="Ver Escritos">
                    <span>
                      <IconButton
                        onClick={() => onPreviewAttachment(a.id)}
                        disabled={
                          !hasPermission(
                            "descargar archivos adjuntos expedientes",
                          )
                        }
                        sx={{
                          borderRadius: 2,
                          bgcolor: "rgba(0,0,0,0.04)",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                          flexShrink: 0,
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Movimientos">
                    <span>
                      <IconButton
                        onClick={() => openMovements(a)}
                        sx={{
                          borderRadius: 2,
                          bgcolor: "rgba(0,0,0,0.04)",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                        }}
                      >
                        <TimelineOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </GlassCard>
            );
          })}
        </Box>
      )}

      <AppDialog
        open={openUploadSingleDialog}
        onClose={() => setOpenUploadSingleDialog(false)}
        title="Subir escrito en línea"
        maxWidth="md"
        fullWidth
        fullScreenMobile
        paperSx={{ minHeight: "80vh" }}
      >
        <UploadSacAttachmentSingle
          legalFile={legalFile}
          onDone={() => {
            setOpenUploadSingleDialog(false);
            onRefresh();
          }}
        />
      </AppDialog>

      <AttachmentMovementsDialog
        open={movementsOpen}
        onClose={closeMovements}
        attachment={selectedAttachment}
      />
    </>
  );
}
